/**
 * Catalog for the Cosmos Brain Power hub.
 * Games are described once here and grouped into categories so the same game
 * can appear in more than one lane (feels broader) without duplicating code.
 */

export const ACCENTS = {
  cyan: { text: 'text-cyan-300', border: 'border-cyan-500/40', bg: 'bg-cyan-950/50', grad: 'from-cyan-500 to-blue-600', glow: 'shadow-cyan-500/20', hex: '#22d3ee' },
  emerald: { text: 'text-emerald-300', border: 'border-emerald-500/40', bg: 'bg-emerald-950/50', grad: 'from-emerald-500 to-teal-600', glow: 'shadow-emerald-500/20', hex: '#34d399' },
  amber: { text: 'text-amber-300', border: 'border-amber-500/40', bg: 'bg-amber-950/50', grad: 'from-amber-500 to-orange-600', glow: 'shadow-amber-500/20', hex: '#fbbf24' },
  sky: { text: 'text-sky-300', border: 'border-sky-500/40', bg: 'bg-sky-950/50', grad: 'from-sky-500 to-blue-600', glow: 'shadow-sky-500/20', hex: '#38bdf8' },
  orange: { text: 'text-orange-300', border: 'border-orange-500/40', bg: 'bg-orange-950/50', grad: 'from-orange-500 to-amber-600', glow: 'shadow-orange-500/20', hex: '#fb923c' },
  lime: { text: 'text-lime-300', border: 'border-lime-500/40', bg: 'bg-lime-950/50', grad: 'from-lime-500 to-emerald-600', glow: 'shadow-lime-500/20', hex: '#a3e635' },
  purple: { text: 'text-purple-300', border: 'border-purple-500/40', bg: 'bg-purple-950/50', grad: 'from-purple-500 to-fuchsia-600', glow: 'shadow-purple-500/20', hex: '#c084fc' },
  rose: { text: 'text-rose-300', border: 'border-rose-500/40', bg: 'bg-rose-950/50', grad: 'from-rose-500 to-pink-600', glow: 'shadow-rose-500/20', hex: '#fb7185' },
  slate: { text: 'text-slate-300', border: 'border-slate-700', bg: 'bg-slate-800/50', grad: 'from-slate-600 to-slate-700', glow: 'shadow-slate-500/20', hex: '#94a3b8' },
}

/**
 * `id` is what App routes on. `gameId` is the playable engine.
 * `meta` is a short badge that hints at volume of content.
 */
export const GAMES = {
  detective: {
    gameId: 'detective', name: 'El Detective', emoji: '🕵️', accent: 'cyan',
    tagline: 'Deduce al culpable con lógica pura.',
    trains: 'Pensamiento crítico',
    modes: ['Caso único', 'Noche de Copas', '5 categorías', '4 dificultades'],
    meta: 'Casos infinitos', infinite: true,
  },
  eco: {
    gameId: 'eco', name: 'Eco', emoji: '🌊', accent: 'emerald',
    tagline: 'Repite secuencias que crecen.',
    trains: 'Memoria de trabajo',
    modes: ['Dígitos', 'Inverso', 'Patrón visual', 'Palabras'],
    meta: '4 modos', infinite: true,
  },
  duelo: {
    gameId: 'duelo', name: 'Duelo de Cultura', emoji: '⚔️', accent: 'amber',
    tagline: '7 preguntas contra un rival con Elo.',
    trains: 'Cultura general',
    modes: ['Partida rápida', 'Retar a un amigo', '6 categorías'],
    meta: 'Rating Elo', infinite: true,
  },
  rafaga: {
    gameId: 'rafaga', name: 'Ráfaga', emoji: '⚡', accent: 'sky',
    tagline: 'Fichas de cultura: imagen, nombre y dato.',
    trains: 'Cultura · Retención',
    modes: ['Fichas', 'Pon a prueba', 'ES/EN'],
    meta: '22 fichas',
  },
  refranero: {
    gameId: 'refranero', name: 'El Refranero', emoji: '📖', accent: 'amber',
    tagline: 'Completa el refrán.',
    trains: 'Memoria remota · Lenguaje',
    modes: ['Refranes', 'Pista'],
    meta: '18 refranes',
  },
  cronicas: {
    gameId: 'cronicas', name: 'Crónicas', emoji: '🕰️', accent: 'orange',
    tagline: 'Ordena la historia en el tiempo.',
    trains: 'Cultura histórica',
    modes: ['Línea de tiempo'],
    meta: '22 eventos',
  },
  bazar: {
    gameId: 'bazar', name: 'El Bazar', emoji: '⚖️', accent: 'lime',
    tagline: 'Estima la magnitud. Piénsalo, no lo sepas.',
    trains: 'Pensamiento crítico',
    modes: ['Fermi', 'Por cercanía'],
    meta: '14 retos',
  },
  melodias: {
    gameId: 'melodias', name: 'Melodías', emoji: '🎵', accent: 'purple',
    tagline: 'Reconoce y recuerda de oído.',
    trains: 'Memoria auditiva',
    modes: ['Adivina la melodía', 'Repite el tono'],
    meta: '6 melodías',
  },
  math: {
    gameId: 'math', name: 'Neuro-Math Blitz', emoji: '➗', accent: 'slate',
    tagline: 'Cálculo supersónico.',
    trains: 'Velocidad de cálculo',
    modes: ['Blitz'], meta: 'Arcade',
  },
  fakenews: {
    gameId: 'fakenews', name: 'Fake News Slayer', emoji: '🛡️', accent: 'slate',
    tagline: 'Desarma titulares trampa.',
    trains: 'Alfabetización mediática',
    modes: ['Caza bulos'], meta: 'Arcade',
  },
  critical: {
    gameId: 'critical', name: 'Pensamiento Crítico', emoji: '⚖️', accent: 'slate',
    tagline: 'Detecta falacias lógicas.',
    trains: 'Argumentación',
    modes: ['Duelo de lógica'], meta: 'Arcade',
  },
  dockpuzzle: {
    gameId: 'dockpuzzle', name: 'Puzzle del Dock', emoji: '🧩', accent: 'slate',
    tagline: 'Ordena el panel con un hueco libre.',
    trains: 'Memoria espacial',
    modes: ['Escape'], meta: 'Arcade',
  },
  pulso: {
    gameId: 'pulso', name: 'Pulso', emoji: '◎', accent: 'cyan',
    tagline: 'Sigue la órbita y repítela. Se acaba.',
    trains: 'Atención',
    modes: ['4 rondas'], meta: 'Órbita',
  },
  sombra: {
    gameId: 'sombra', name: 'Sombra', emoji: '▦', accent: 'emerald',
    tagline: 'La imagen se apaga. Reconstrúyela.',
    trains: 'Memoria espacial',
    modes: ['5 patrones'], meta: 'Órbita',
  },
  filtro: {
    gameId: 'filtro', name: 'Filtro', emoji: '◇', accent: 'amber',
    tagline: 'Elige la frase que sí se puede defender.',
    trains: 'Lectura crítica',
    modes: ['6 pares'], meta: 'Órbita',
  },
  ancla: {
    gameId: 'ancla', name: 'Ancla', emoji: '✶', accent: 'purple',
    tagline: 'La palabra justa, no la primera.',
    trains: 'Lenguaje',
    modes: ['6 frases'], meta: 'Órbita',
  },
  cierre: {
    gameId: 'cierre', name: 'Cierre', emoji: '◌', accent: 'rose',
    tagline: 'Foco de 20 segundos. Luego se cierra solo.',
    trains: 'Atención con final',
    modes: ['Una sesión'], meta: 'Órbita',
  },
  ajedrez: {
    gameId: 'ajedrez', name: 'Ajedrez', emoji: '♔', accent: 'amber',
    tagline: 'Una partida legal. Se acaba en mate o en tablas.',
    trains: 'Planificación',
    modes: ['Blancas', 'Reglas completas'], meta: 'Una mesa',
  },
}

export const CATEGORIES = [
  {
    id: 'orbita', label: 'Órbita', emoji: '✦', accent: 'cyan',
    blurb: 'Juegos con final. Enseñan y se acaban.',
    items: ['pulso', 'sombra', 'filtro', 'ancla', 'cierre', 'ajedrez'],
  },
  {
    id: 'destacados', label: 'Destacados', emoji: '✨', accent: 'cyan',
    blurb: 'Lo mejor para empezar hoy.',
    items: ['detective', 'eco', 'duelo', 'rafaga'],
  },
  {
    id: 'memoria', label: 'Memoria', emoji: '🧠', accent: 'emerald',
    blurb: 'Recuerda, repite, retén.',
    items: ['eco', 'melodias', 'refranero'],
  },
  {
    id: 'critico', label: 'Pensamiento crítico', emoji: '🔍', accent: 'cyan',
    blurb: 'Deduce y razona, no adivines.',
    items: ['detective', 'bazar', 'critical'],
  },
  {
    id: 'cultura', label: 'Cultura general', emoji: '🌍', accent: 'amber',
    blurb: 'Historia, arte, ciencia y geografía.',
    items: ['duelo', 'rafaga', 'cronicas', 'refranero'],
  },
  {
    id: 'grupo', label: 'Para jugar en grupo', emoji: '🍸', accent: 'rose',
    blurb: 'Noche de copas y retos a amigos.',
    items: ['detective', 'duelo'],
  },
  {
    id: 'arcade', label: 'Arcade', emoji: '🕹️', accent: 'slate',
    blurb: 'Reflejos y entrenamientos clásicos.',
    items: ['math', 'fakenews', 'critical', 'dockpuzzle'],
  },
]

/** Every id the randomizer may launch (deduped). */
export const PLAYABLE_IDS = ['ajedrez', 'pulso', 'sombra', 'filtro', 'ancla', 'cierre', 'detective', 'eco', 'duelo', 'rafaga', 'refranero', 'cronicas', 'bazar', 'melodias', 'math', 'fakenews', 'critical', 'dockpuzzle']

const EN = {
  detective: { name: 'The Detective', trains: 'Critical thinking' },
  eco: { name: 'Echo', trains: 'Working memory' },
  duelo: { name: 'Culture Duel', trains: 'General knowledge' },
  rafaga: { name: 'Burst', trains: 'Culture · Retention' },
  refranero: { name: 'Sayings', trains: 'Remote memory · Language' },
  cronicas: { name: 'Chronicles', trains: 'Historical culture' },
  bazar: { name: 'The Bazaar', trains: 'Critical thinking' },
  melodias: { name: 'Melodies', trains: 'Auditory memory' },
  math: { name: 'Neuro-Math Blitz', trains: 'Calculation speed' },
  fakenews: { name: 'Fake News Slayer', trains: 'Media literacy' },
  critical: { name: 'Critical Thinking', trains: 'Argument' },
  dockpuzzle: { name: 'Dock Puzzle', trains: 'Spatial memory' },
  pulso: { name: 'Pulse', trains: 'Attention' },
  sombra: { name: 'Shadow', trains: 'Spatial memory' },
  filtro: { name: 'Filter', trains: 'Critical reading' },
  ancla: { name: 'Anchor', trains: 'Language' },
  cierre: { name: 'Close', trains: 'Attention with an end' },
  ajedrez: { name: 'Chess', trains: 'Planning' },
}

const CATEGORY_EN = {
  orbita: { label: 'Orbit', blurb: 'Games that end. They teach, then they stop.' },
  destacados: { label: 'Featured', blurb: 'The best place to start today.' },
  memoria: { label: 'Memory', blurb: 'Remember, repeat, retain.' },
  critico: { label: 'Critical thinking', blurb: 'Deduce and reason. Don’t guess.' },
  cultura: { label: 'General knowledge', blurb: 'History, art, science, and geography.' },
  grupo: { label: 'Play together', blurb: 'Game night and challenges with friends.' },
  arcade: { label: 'Arcade', blurb: 'Reflexes and classic drills.' },
}

export function gameText(game, lang) {
  if (lang !== 'en') return game
  return { ...game, ...(EN[game.gameId] || {}) }
}

export function categoryText(cat, lang) {
  if (lang !== 'en') return cat
  return { ...cat, ...(CATEGORY_EN[cat.id] || {}) }
}

export function accent(a) {
  return ACCENTS[a] || ACCENTS.slate
}
